/** Hinged galvanised gate that closes the one deliberate break in a pen kerb. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

export const PEN_GATE = {
  length: 1,
  /** The leaf. This is what the collider is, and what she cannot get past. */
  height: 0.62,
  thickness: 0.05,
  /**
   * How high the *mesh* reaches — the latch pin stands above the free stile.
   *
   * Deliberately a second number rather than a taller `height`. The pin is
   * 90 mm of outline whose whole job is to be visible at 64 px (it is the thing
   * the beat is named after); giving it collision would make a gate she can
   * neither pass nor climb 90 mm taller for no reason anybody could see. The
   * placement gate in `zone-bounds.spec.ts` measures the mesh, so it reads this
   * one, and the physics reads `height`.
   */
  meshHeight: 0.71,
} as const;

export interface PenGateLeaf {
  /** Radians about Y. Applied to the mesh group and the collider alike. */
  rotY: number;
  /** World centre of the leaf — which is also where its mesh group goes. */
  pos: { x: number; y: number; z: number };
  /**
   * Half-extents in the leaf's own frame: x along its length, z its thickness.
   * They are world half-extents only when `rotY` is a multiple of π.
   */
  half: { x: number; y: number; z: number };
}

/**
 * Where the leaf is and how it is turned, for a hinge and an open fraction.
 *
 * **The single source of truth for the gate's pose in the plane it moves in.**
 * The mesh is placed from `pos.x`, `pos.z` and `rotY`; the collider is built from
 * the same three plus `pos.y` and `half`. That is the strongest form of the rule
 * this room keeps breaking (D-056, D-059): what you can see and what stops you
 * are not merely derived from one list, they *are* one transform.
 *
 * **`pos.y` is the exception and it is deliberate**, flagged on independent
 * review because an earlier version of this note claimed all three axes. The
 * collider is a box and wants its CENTRE, 0.31 m up; the mesh is a kit asset and
 * every kit asset in this project has its origin at ground contact, so it is
 * placed at y = 0. Two different conventions, both correct, and the gap between
 * them is exactly `height / 2`.
 *
 * That is safe because it is not a coincidence anybody has to remember:
 * `asset.spec.ts` asserts the ground-contact origin for **every** registered
 * asset, so the day this one stops being ground-origin is the day that gate goes
 * red. A gate hung 0.31 m in the air is not a subtle failure.
 *
 * ── Why the mesh is centred and not hinged, which is not what it looks like ───
 *
 * A gate wants its pivot at its origin — swing it about anything else and the
 * leaf orbits an invented point. The first version of this asset was authored
 * that way, spanning local x ∈ [0, 1] with the hinge at zero, and it failed
 * `asset.spec.ts`'s origin-and-footprint contract: *"asset pen-gate is
 * off-centre in X — expected < 0.02, received 0.520"*. That gate is house-wide
 * and it is not a formality — it is what makes every kit part independently
 * placeable and every `shots:asset` turnaround comparable.
 *
 * The resolution is that a hinged origin was never actually needed. This
 * function already computes the leaf's **centre** for any angle, so the mesh is
 * authored centred like every other kit part and simply placed *there*, with the
 * same `rotY` the collider gets. The rotation about the hinge is expressed in
 * `pos`, where it belongs, and nothing in the room has to know that a gate is a
 * special kind of object. An exemption in `asset.spec.ts` would have bought the
 * same picture and cost the contract.
 */
export function penGateLeaf(
  hinge: { x: number; z: number },
  swing: -1 | 1,
  open: number,
): PenGateLeaf {
  const clamped = Math.min(1, Math.max(0, open));
  const rotY = swing * (1 - clamped) * Math.PI / 2;
  return {
    rotY,
    pos: {
      x: hinge.x + Math.cos(rotY) * PEN_GATE.length / 2,
      y: PEN_GATE.height / 2,
      z: hinge.z - Math.sin(rotY) * PEN_GATE.length / 2,
    },
    half: { x: PEN_GATE.length / 2, y: PEN_GATE.height / 2, z: PEN_GATE.thickness / 2 },
  };
}

const zone: ZonePalette = (() => {
  const found = getZone('farrowing');
  if (!found) throw new Error('farrowing zone missing from the palette');
  return found;
})();

function pipe(radius: number, length: number, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 8, 1), surface('metal', 0xaeb7b9));
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

/**
 * The hinge end, in local coordinates. The free end is `+HALF_LENGTH`.
 *
 * Centred on the footprint like every other kit part — see `penGateLeaf`'s note
 * on why the pivot does not live here.
 */
const HALF_LENGTH = PEN_GATE.length / 2;

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-gate';

  const panel = new THREE.Mesh(
    new THREE.BoxGeometry(PEN_GATE.length, 0.3, 0.035),
    surface('mineral', zone.surfaces[2] ?? 0x8d918c),
  );
  panel.name = 'solid-panel';
  panel.position.set(0, 0.15, 0);
  panel.castShadow = true;
  group.add(panel);

  for (const [y, name] of [[0.44, 'bar-lower'], [0.6, 'bar-top']] as const) {
    const bar = pipe(0.018, 0.96, name);
    bar.rotation.z = Math.PI / 2;
    bar.position.set(0, y, 0);
    group.add(bar);
  }
  for (const [x, name] of [[-HALF_LENGTH + 0.02, 'stile-hinge'], [HALF_LENGTH - 0.02, 'stile-free']] as const) {
    const stile = pipe(0.022, 0.62, name);
    stile.position.set(x, 0.31, 0);
    group.add(stile);
  }

  // The pin rises above the free stile and the keeper protrudes toward the
  // walkway. That broken outline reads as a latch from Rosalie's height; a flat
  // plate beside the stile read as a shading seam instead.
  const pin = pipe(0.014, 0.16, 'latch-pin');
  pin.position.set(HALF_LENGTH + 0.015, 0.63, 0.025);
  group.add(pin);
  const keeper = new THREE.Mesh(new THREE.BoxGeometry(0.065, 0.08, 0.09), surface('metal', 0xaeb7b9));
  keeper.name = 'latch-keeper';
  // Inboard of the free stile, not outboard of it — the pin drops THROUGH the
  // keeper, which is how a drop-catch works and is also the 4 mm that brings the
  // whole part back inside `asset.spec.ts`'s 20 mm centring tolerance. It was
  // 0.02025 off, and the keeper was the entire reason.
  keeper.position.set(HALF_LENGTH - 0.01, 0.53, 0.025);
  keeper.castShadow = true;
  group.add(keeper);

  return group;
}

export const penGateAsset: AssetDefinition = {
  id: 'pen-gate',
  label: 'Pen gate',
  description: 'Low hinged galvanized pen gate with a solid lower panel.',
  // Width and height are the MESH's, not the leaf's: the latch pin stands proud
  // of both, and a footprint that describes only the panel is a footprint that
  // reports a smaller object than the one that gets drawn.
  footprint: { width: PEN_GATE.length + 0.06, height: PEN_GATE.meshHeight, length: PEN_GATE.thickness + 0.04 },
  create: build,
};
